import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Activity, CheckCircle2, XCircle, RefreshCw, Webhook, Coins } from "lucide-react";

const OUTCOME_STYLES = {
  processed: "bg-[#10B981]/10 text-[#10B981]",
  failed: "bg-[#EF4444]/10 text-[#EF4444]",
  ignored: "bg-slate-100 text-[#94A3B8]",
  processing: "bg-[#F59E0B]/10 text-[#F59E0B]",
};

const OutcomeBadge = ({ outcome }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz uppercase tracking-wider ${OUTCOME_STYLES[outcome] || OUTCOME_STYLES.ignored}`}>
    {outcome || "legacy"}
  </span>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const SummaryCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${tone}`} />
      <p className="font-mono-kz text-xs text-[#94A3B8] tracking-widest font-bold uppercase">{label}</p>
    </div>
    <p className="font-display text-3xl font-black text-[#0B1B3D] mt-2">{value}</p>
  </div>
);

export const AdminWebhookEvents = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResults, setReconcileResults] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/webhook-events?limit=100");
      setData(r.data);
    } catch {
      toast.error("Could not load webhook events");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/admin/reconcile-payments");
      setReconcileResults(r.data);
      if (r.data.checked === 0) toast.info("No pending payments to reconcile");
      else if (r.data.credited > 0) toast.success(`${r.data.credited} payment(s) credited!`);
      else toast.info(`${r.data.checked} pending payment(s) checked — none credit-eligible yet`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  if (!data) return <div className="text-[#94A3B8]">Loading…</div>;
  const { events, summary } = data;

  return (
    <div data-testid="webhook-health-panel" className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={Activity} label="Events (7d)" value={summary.total_7d} tone="text-[#0EA5E9]" testid="webhook-stat-total" />
        <SummaryCard icon={CheckCircle2} label="Processed (7d)" value={summary.processed_7d} tone="text-[#10B981]" testid="webhook-stat-processed" />
        <SummaryCard icon={XCircle} label="Failed (7d)" value={summary.failed_7d} tone="text-[#EF4444]" testid="webhook-stat-failed" />
        <SummaryCard icon={Webhook} label="Failed (all time)" value={summary.failed_total} tone="text-[#F59E0B]" testid="webhook-stat-failed-total" />
      </div>

      <div data-testid="reconcile-panel" className="bg-white border border-slate-200 rounded-xl px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-[#0EA5E9]" />
              <h3 className="font-display font-extrabold text-[#0B1B3D]">Payment reconciliation</h3>
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">Queries Stripe directly for pending purchases and credits any that were actually paid (idempotent — safe to run anytime).</p>
          </div>
          <button data-testid="reconcile-payments-btn" onClick={reconcile} disabled={reconciling}
            className="inline-flex items-center gap-1.5 bg-[#0B1B3D] hover:bg-[#0EA5E9] disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-lg font-display transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${reconciling ? "animate-spin" : ""}`} /> {reconciling ? "Checking Stripe…" : "Reconcile pending payments"}
          </button>
        </div>
        {reconcileResults && reconcileResults.results.length > 0 && (
          <div data-testid="reconcile-results" className="mt-3 border-t border-slate-100 pt-3 space-y-1.5">
            {reconcileResults.results.map((r) => (
              <div key={r.stripe_session_id} className="flex items-center gap-3 text-sm">
                <OutcomeBadge outcome={r.result === "credited" ? "processed" : r.result === "stripe_error" || r.result === "refused" ? "failed" : "processing"} />
                <span className="font-bold text-[#0B1B3D]">{r.pack_name}</span>
                <span className="text-[#475569]">€{r.amount_eur}</span>
                <span className="font-mono-kz text-xs text-[#94A3B8]">{r.result}{r.credits ? ` · +${r.credits} cr` : ""}</span>
                {r.detail && <span className="font-mono-kz text-xs text-[#EF4444] truncate max-w-[380px]" title={r.detail}>{r.detail}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {summary.last_failure && (
        <div data-testid="webhook-last-failure" className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-xl px-4 py-3 text-sm">
          <span className="font-bold text-[#EF4444]">Last failure:</span>{" "}
          <span className="text-[#475569]">{summary.last_failure.event_type} · {fmtDate(summary.last_failure.received_at)}</span>
          {summary.last_failure.error && <p className="font-mono-kz text-xs text-[#EF4444] mt-1 break-all">{summary.last_failure.error}</p>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <Webhook className="w-4 h-4 text-[#0EA5E9]" />
            <h2 className="font-display font-extrabold text-[#0B1B3D]">Stripe webhook events</h2>
            <span className="font-mono-kz text-xs text-[#94A3B8]">last event: {fmtDate(summary.last_event_at)}</span>
          </div>
          <button data-testid="webhook-refresh-btn" onClick={load} disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#475569] hover:text-[#0B1B3D] disabled:opacity-40 border border-slate-200 px-3 py-1.5 rounded-lg">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {events.length === 0 ? (
          <p data-testid="webhook-empty-state" className="p-6 text-sm text-[#94A3B8]">No webhook events received yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="webhook-events-table">
              <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
                <th className="p-3">Received</th><th className="p-3">Event type</th><th className="p-3">Outcome</th><th className="p-3">Session</th><th className="p-3">Retries</th><th className="p-3">Error</th>
              </tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.event_id} data-testid={`webhook-event-row-${e.event_id}`} className="border-t border-slate-100">
                    <td className="p-3 whitespace-nowrap text-[#475569]">{fmtDate(e.received_at)}</td>
                    <td className="p-3 font-mono-kz text-xs text-[#0B1B3D]">{e.event_type}</td>
                    <td className="p-3"><OutcomeBadge outcome={e.outcome} /></td>
                    <td className="p-3 font-mono-kz text-xs text-[#94A3B8] max-w-[180px] truncate" title={e.session_id || ""}>{e.session_id || "—"}</td>
                    <td className="p-3 text-[#475569]">{e.duplicate_count || 0}</td>
                    <td className="p-3 font-mono-kz text-xs text-[#EF4444] max-w-[260px] truncate" title={e.error || ""}>{e.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
